// frontend/src/pages/KnShipmentDetail.jsx
//
// EK-D17 — eine gespiegelte K&N-Sendung im Detail. NUR LESEND.
//
// 🔴 Kein Eingabefeld, kein Speichern-Knopf. Der Stand ist gespiegelt; wer ihn
//    aendern will, aendert ihn bei Kuehne+Nagel, nicht hier.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert, Box, Chip, Divider, Skeleton, Stack, Typography,
} from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import LeistungsHinweis from '../components/kn/LeistungsHinweis'
import { api } from '../api/client'
import Panel from '../components/common/Panel'
import { SP, FS, DETAIL_RAIL_WIDTH } from '../theme/tokens'
import { formatBerlinDate, formatBerlinDateTime } from '../utils/time'
import { STRICH, gewichtText, ortText, stufeText, wertOderStrich } from '../utils/knAnzeige'

/** Eine Zeile „Beschriftung — Wert". Ein fehlender Wert ist ein Strich, nie eine 0. */
function Zeile({ label, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={SP.card} sx={{ py: SP.chip }}>
      <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: FS.base, textAlign: 'right' }}>
        {children}
      </Typography>
    </Stack>
  )
}

function Termin({ zeitpunkt, art, grund }) {
  if (!zeitpunkt) {
    return (
      <Stack direction="row" gap={SP.chip} alignItems="center" justifyContent="flex-end">
        <Typography component="span" sx={{ color: 'text.secondary' }}>{STRICH}</Typography>
        {grund && (
          <Typography component="span" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            {grund}
          </Typography>
        )}
      </Stack>
    )
  }
  return (
    <Stack direction="row" gap={SP.chip} alignItems="center" justifyContent="flex-end">
      <span>{formatBerlinDate(zeitpunkt)}</span>
      <Chip
        size="small"
        variant="outlined"
        color={art === 'erreicht' ? 'success' : 'default'}
        label={art === 'erreicht' ? 'Actual' : 'Planned'}
        sx={{ height: 20, fontSize: FS.xxs }}
      />
    </Stack>
  )
}

function Abschnitt({ titel, children }) {
  return (
    <Panel>
      <Typography sx={{ fontSize: FS.lg, fontWeight: 600, mb: SP.tight }}>{titel}</Typography>
      <Divider sx={{ mb: SP.tight }} />
      {children}
    </Panel>
  )
}

export default function KnShipmentDetail() {
  const { id } = useParams()
  const [sendung, setSendung] = useState(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    let abgemeldet = false
    setLaedt(true)
    api.get(`/api/kn/shipments/${id}`)
      .then(({ data }) => { if (!abgemeldet) { setSendung(data); setFehler(null) } })
      .catch((e) => {
        if (abgemeldet) return
        setFehler(e?.response?.status === 404
          ? 'This shipment does not exist in the mirror.'
          : (e?.message || 'Unknown error'))
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true }
  }, [id])

  // 🔴 Steht `del` nicht in den bestellten Leistungen, sagt die Zustellzeile
  //    WARUM sie leer ist. Ein blosser Strich laese offen, ob K&N den Termin
  //    nur noch nicht gemeldet hat.
  const del_fehlt = (sendung?.leistungsbild?.fehlend || [])
    .some((f) => f.code === 'DEL')

  return (
    <PageContainer>
      <ViewHeader
        breadcrumbs={[{ label: 'K&N Shipments', href: '/kn-shipments' }]}
        // 🔴 B3 (2026-10-06): `backLink` ist ein OBJEKT ({label, href}), kein Pfad.
        // Hier stand eine nackte Zeichenkette — mobil wie (seit B3) desktop las
        // `ViewHeader` `backLink.label`/`.href` als `undefined`, der Rueckweg war
        // ein pfeilloser, unbeschrifteter Knopf. Mit dem Desktop-Zweig waere der
        // Fehler auf beiden Breiten sichtbar geworden.
        backLink={{ label: 'Back to shipments', href: '/kn-shipments' }}
        title={sendung ? sendung.tracking_number : 'Shipment'}
        subtitle={sendung ? `As of ${formatBerlinDateTime(sendung.fetched_at)}` : undefined}
      />

      {fehler && <Alert severity="error" sx={{ mb: SP.card }}>{fehler}</Alert>}

      {laedt && (
        <Stack spacing={SP.card}>
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      )}

      {!laedt && sendung && (
        <>
          <LeistungsHinweis bild={sendung.leistungsbild} />

          <Box sx={{ display: 'flex', gap: SP.md, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Hauptspalte */}
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <Stack spacing={SP.card}>
                <Abschnitt titel="Where is it">
                  <Zeile label="Stage">{stufeText(sendung.current_stage)}</Zeile>
                  <Zeile label="Origin">
                    {ortText(sendung.origin_code, sendung.origin_name)}
                  </Zeile>
                  <Zeile label="Destination">
                    {ortText(sendung.destination_code, sendung.destination_name)}
                  </Zeile>
                  <Zeile label="Vessel">{wertOderStrich(sendung.vessel_name)}</Zeile>
                  <Zeile label="Voyage">{wertOderStrich(sendung.voyage_number)}</Zeile>
                </Abschnitt>

                <Abschnitt titel="When does it arrive">
                  <Zeile label="Departure from port of loading">
                    {/* 🔴 `art="erreicht"` ist hier fest, und das ist nur
                        richtig, WEIL `mapping.TERMINQUELLEN["departed_at"]`
                        ausschliesslich aus `condition == "ACTUAL"` speist.
                        Bekaeme die Quelle je eine PLANNED-Bedingung, zeigte
                        diese Zeile Plan als Ist — genau das, wogegen
                        `_BEDINGUNG_ZU_ART` serverseitig antritt.
                        Gepinnt von `test_departed_at_ist_immer_ACTUAL`. */}
                    <Termin zeitpunkt={sendung.departed_at} art="erreicht" />
                  </Zeile>
                  {/* 🔴 ZWEI getrennte Zeilen. Die Hafenankunft ist nicht die
                      Zustellung -- sie hat bewusst keine Spalte in der
                      Datenbank und steht in `raw`. */}
                  <Zeile label="Port arrival">
                    <Termin
                      zeitpunkt={sendung.hafenankunft?.zeitpunkt}
                      art={sendung.hafenankunft?.art}
                    />
                  </Zeile>
                  <Zeile label="Final delivery">
                    <Termin
                      zeitpunkt={sendung.arrived_at || sendung.eta_at}
                      art={sendung.arrived_at ? 'erreicht' : 'geplant'}
                      grund={del_fehlt ? 'not in the ordered services' : undefined}
                    />
                  </Zeile>
                  {sendung.hafenankunft?.ort_code && (
                    <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: SP.chip }}>
                      Port: {ortText(sendung.hafenankunft.ort_code, sendung.hafenankunft.ort_name)}
                    </Typography>
                  )}
                </Abschnitt>

                <Abschnitt titel="Dimensions">
                  <Zeile label="Gross weight">{gewichtText(sendung.weight_gross_g)}</Zeile>
                  <Zeile label="Volume">{wertOderStrich(sendung.volume_cbm, 'cbm')}</Zeile>
                  <Zeile label="Packages">{wertOderStrich(sendung.packages_count)}</Zeile>
                  <Zeile label="Dangerous goods">
                    {sendung.has_dangerous_goods === null ||
                     sendung.has_dangerous_goods === undefined
                      ? STRICH
                      : (sendung.has_dangerous_goods ? 'yes' : 'no')}
                  </Zeile>
                </Abschnitt>
              </Stack>
            </Box>

            {/* Seitenspalte — im DOM NACH der Hauptspalte, damit sie auf dem
                Telefon darunter steht (Pattern Library §Detail Page). */}
            {/* 🔴 G0b.5 (2026-10-13): Rail-Literal 320 → `DETAIL_RAIL_WIDTH.narrow`
                (Konzept §2.3:335, Plan §3.3). Wert unveraendert.
                🔴 `data-testid="detail-rail"` ist ein STUMMES DOM-Merkmal (kein
                Layout, keine Rolle) — die PW-Proben 9/9b/12 benennen die Rail
                damit, dieselbe Bauart wie `data-reason` an `EmptyState` (G0.2). */}
            <Box data-testid="detail-rail" sx={{ width: { xs: '100%', md: DETAIL_RAIL_WIDTH.narrow }, flexShrink: 0 }}>
              <Stack spacing={SP.card}>
                <Abschnitt titel="Booking">
                  <Zeile label="Mode of transport">
                    {wertOderStrich(sendung.mode_of_transport)}
                  </Zeile>
                  <Zeile label="Service type">{wertOderStrich(sendung.service_type)}</Zeile>
                  <Zeile label="Movement type">{wertOderStrich(sendung.movement_type)}</Zeile>
                  <Zeile label="Incoterm">
                    {sendung.incoterm_code
                      ? `${sendung.incoterm_code}${sendung.incoterm_place ? ` ${sendung.incoterm_place}` : ''}`
                      : STRICH}
                  </Zeile>
                  {/* Der EK-D4-Anker. Heute leer, weil `carriers` 0 Zeilen hat
                      und der Abzug ihn nur bei GENAU EINER mykn-Zeile setzt. */}
                  <Zeile label="Carrier">{wertOderStrich(sendung.carrier_name)}</Zeile>
                </Abschnitt>

                <Abschnitt titel="Consignee at K&N">
                  <Zeile label="Name">{wertOderStrich(sendung.empfaenger?.name)}</Zeile>
                  <Zeile label="City">{wertOderStrich(sendung.empfaenger?.ort)}</Zeile>
                  <Zeile label="Country">{wertOderStrich(sendung.empfaenger?.land)}</Zeile>
                  <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: SP.chip }}>
                    This is how Kühne+Nagel holds the consignee. If the city
                    differs from the warehouse, that is a finding for the master
                    data at K&amp;N.
                  </Typography>
                </Abschnitt>

                <Abschnitt titel="References">
                  <Zeile label="K&N shipment no.">
                    {wertOderStrich(sendung.source_shipment_id)}
                  </Zeile>
                  <Zeile label="Bill of lading">
                    {(sendung.obl_numbers || []).join(', ') || STRICH}
                  </Zeile>
                  <Zeile label="Customer references">
                    {(sendung.customer_references || []).join(', ') || STRICH}
                  </Zeile>
                  <Zeile label="Created at K&N">
                    {formatBerlinDateTime(sendung.shipment_created_at)}
                  </Zeile>
                </Abschnitt>
              </Stack>
            </Box>
          </Box>

          <Typography sx={{ mt: SP.card, fontSize: FS.md, color: 'text.secondary' }}>
            {STRICH} means: Kühne+Nagel supplied nothing for this. This state is
            pulled once per night and is not edited here.
          </Typography>
        </>
      )}
    </PageContainer>
  )
}
